import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { api } from '../../shared/api';
import { appModules, defaultModulePermissions, normalizeModulePermissions, type ModuleId, type ModulePermissionMap, type StaffRole } from '../../shared/modulePermissions';
import { useAuth } from '../auth/AuthProvider';

export function RolePermissionsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdministrator = user?.groups.includes('Administrator') ?? false;
  const permissionsQuery = useQuery({ queryKey: ['staff-role-permissions'], queryFn: api.staffRolePermissions.get, enabled: isAdministrator });
  const rolesQuery = useQuery({ queryKey: ['staff-roles'], queryFn: api.staffRoles.list, enabled: isAdministrator });
  const configurableRoles = (rolesQuery.data?.items ?? []).filter((role) => role !== 'Administrator');
  const [permissions, setPermissions] = useState<ModulePermissionMap>(() => defaultModulePermissions());
  const [saved, setSaved] = useState(false);
  const savePermissions = useMutation({
    mutationFn: (nextPermissions: ModulePermissionMap) => api.staffRolePermissions.update({ permissions: nextPermissions }),
    onSuccess: async (savedPermissions) => {
      setPermissions(normalizeModulePermissions(savedPermissions.permissions));
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['staff-role-permissions'] });
    },
  });

  useEffect(() => {
    if (permissionsQuery.data) setPermissions(normalizeModulePermissions(permissionsQuery.data.permissions));
  }, [permissionsQuery.data]);

  if (!isAdministrator) return <ErrorState message="Solo administradores pueden editar permisos." />;

  const toggle = (moduleId: ModuleId, role: StaffRole) => {
    setPermissions((current) => {
      const roles = current[moduleId] ?? [];
      const nextRoles = roles.includes(role) ? roles.filter((item) => item !== role) : [...roles, role];
      return { ...current, [moduleId]: ['Administrator', ...nextRoles.filter((item) => item !== 'Administrator')] };
    });
    setSaved(false);
  };
  const reset = () => {
    setPermissions(defaultModulePermissions());
    setSaved(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Permisos por modulo</h1>
        <p className="mt-1 text-sm text-slate-500">Admin siempre ve todo. Configura que modulos pueden ver otros roles.</p>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Matriz de acceso</h2>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={reset}>Restaurar defaults</Button>
              <Button type="button" disabled={savePermissions.isPending} onClick={() => savePermissions.mutate(permissions)}>
                <Save className="h-4 w-4" aria-hidden="true" />
                Guardar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {permissionsQuery.isLoading ? <LoadingState label="Cargando permisos" /> : null}
          {rolesQuery.isLoading ? <LoadingState label="Cargando roles" /> : null}
          {permissionsQuery.isError ? <ErrorState message={permissionsQuery.error.message} /> : null}
          {rolesQuery.isError ? <ErrorState message={rolesQuery.error.message} /> : null}
          {savePermissions.isError ? <ErrorState message={savePermissions.error.message} /> : null}
          {saved ? <Badge className="bg-[#e5f3e4] text-[#2f6b38]">Permisos guardados</Badge> : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead>
                <tr className="border-b border-[#d6ded0]">
                  <th className="px-3 py-2">Modulo</th>
                  <th className="px-3 py-2">Administrator</th>
                  {configurableRoles.map((role) => <th key={role} className="px-3 py-2">{role}</th>)}
                </tr>
              </thead>
              <tbody>
                {appModules.map((module) => (
                  <tr key={module.id} className="border-b border-[#eef3ea]">
                    <td className="px-3 py-3 font-medium text-[#223126]">{module.label}</td>
                    <td className="px-3 py-3"><span className="inline-flex items-center gap-2 text-[#2f6b38]"><ShieldCheck className="h-4 w-4" />Siempre</span></td>
                    {configurableRoles.map((role) => (
                      <td key={role} className="px-3 py-3">
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={permissions[module.id]?.includes(role) ?? false} onChange={() => toggle(module.id, role)} />
                          Ver
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
